import { Badge, severityLabel, severityToBadgeVariant } from "@/components/ui/Badge";
import { formatRelativeTime } from "@/lib/format";
import type { IncidentDetail, IncidentLifecyclePhase } from "@/types";
import { INCIDENT_LIFECYCLE_LABELS } from "@/lib/incidentLifecycle";

interface IncidentWorkspaceHeaderProps {
  detail: IncidentDetail;
  lifecyclePhase: IncidentLifecyclePhase;
}

export function IncidentWorkspaceHeader({ detail, lifecyclePhase }: IncidentWorkspaceHeaderProps) {
  return (
    <header className="iw-header">
      <div className="iw-header__title-row">
        <h3 className="iw-header__title">{detail.title}</h3>
        <div className="iw-header__badges">
          <Badge variant={severityToBadgeVariant(detail.severity)}>{severityLabel(detail.severity)}</Badge>
          <span className="iw-header__status">{INCIDENT_LIFECYCLE_LABELS[lifecyclePhase]}</span>
        </div>
      </div>
      <dl className="iw-header__meta">
        <div className="iw-header__meta-item">
          <dt>Tenant</dt>
          <dd>{detail.tenantName}</dd>
        </div>
        <div className="iw-header__meta-item">
          <dt>Detected</dt>
          <dd>{formatRelativeTime(detail.detectedAt)}</dd>
        </div>
        <div className="iw-header__meta-item">
          <dt>Last verified</dt>
          <dd>{formatRelativeTime(detail.lastVerifiedAt)}</dd>
        </div>
        <div className="iw-header__meta-item">
          <dt>Customer impact</dt>
          <dd>{detail.impact.customerImpact}</dd>
        </div>
        <div className="iw-header__meta-item">
          <dt>Estimated urgency</dt>
          <dd>{detail.impact.estimatedUrgency}</dd>
        </div>
        {detail.policy ? (
          <>
            <div className="iw-header__meta-item">
              <dt>Policy</dt>
              <dd>{policyLabel(detail.policy.policy)}</dd>
            </div>
            <div className="iw-header__meta-item">
              <dt>Policy status</dt>
              <dd>{policyStatusLabel(detail.policy.status)}</dd>
            </div>
            {detail.policy.confidence ? (
              <div className="iw-header__meta-item">
                <dt>Confidence</dt>
                <dd>{detail.policy.confidence}</dd>
              </div>
            ) : null}
          </>
        ) : null}
      </dl>
    </header>
  );
}

function policyLabel(policyId: string): string {
  return policyId
    .split(/[._]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

const POLICY_STATUS_LABELS: Record<string, string> = {
  within_sla: "Within SLA",
  warning: "Warning",
  overdue: "Overdue",
  critical: "Critical",
  disabled: "Not Applicable",
  unknown: "Unknown",
};

function policyStatusLabel(status: string): string {
  return POLICY_STATUS_LABELS[status] ?? policyLabel(status);
}
